import React, { useState } from 'react';
import { MapPinIcon, GlobeAltIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

interface LocationDisplayProps {
  coordinates?: string | null;
  mapUrl?: string | null;
  className?: string;
}

const LocationDisplay: React.FC<LocationDisplayProps> = ({
  coordinates,
  mapUrl,
  className = ''
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const openInGoogleMaps = () => {
    let lat, lng;
    
    if (coordinates) {
      [lat, lng] = coordinates.split(',');
    } else if (mapUrl) {
      // Try to extract coordinates from URL
      const match = mapUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (match) {
        lat = match[1];
        lng = match[2];
      }
    }
    
    if (lat && lng) {
      // Check if mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // For mobile, create multiple URL options
        const urls = [
          `comgooglemaps://?q=${lat},${lng}&center=${lat},${lng}&zoom=17`, // iOS Google Maps
          `google.navigation:q=${lat},${lng}`, // Android Google Maps navigation
          `geo:${lat},${lng}?q=${lat},${lng}`, // Android generic maps
          `https://maps.google.com/?q=${lat},${lng}` // Fallback to web
        ];
        
        // Try each URL in sequence
        let opened = false;
        for (const url of urls) {
          try {
            window.location.href = url;
            opened = true;
            break;
          } catch (e) {
            continue;
          }
        }
        
        // If none worked, fallback to web URL
        if (!opened) {
          window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
        }
      } else {
        // Desktop - open in new tab with marker
        window.open(`https://maps.google.com/?q=${lat},${lng}&z=17`, '_blank');
      }
    } else if (mapUrl) {
      window.open(mapUrl, '_blank');
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  if (!coordinates && !mapUrl) {
    return (
      <div className={`text-sm text-gray-500 italic ${className}`}>
        No location data available
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {coordinates && (
        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-500">Coordinates</p>
              <p className="text-sm font-medium text-gray-900">{coordinates}</p>
            </div>
          </div>
          <button
            onClick={() => copyToClipboard(coordinates, 'coordinates')}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Copy coordinates"
          >
            {copiedField === 'coordinates' ? (
              <CheckIcon className="w-4 h-4 text-green-600" />
            ) : (
              <ClipboardIcon className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
      )}

      {mapUrl && (
        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <GlobeAltIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">Google Maps URL</p>
              <p className="text-sm font-medium text-gray-900 truncate">{mapUrl}</p>
            </div>
          </div>
          <button
            onClick={() => copyToClipboard(mapUrl, 'url')}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors ml-2"
            title="Copy URL"
          >
            {copiedField === 'url' ? (
              <CheckIcon className="w-4 h-4 text-green-600" />
            ) : (
              <ClipboardIcon className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
      )}

      {(coordinates || mapUrl) && (
        <button
          onClick={openInGoogleMaps}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <GlobeAltIcon className="w-5 h-5" />
          <span>Open in Google Maps</span>
        </button>
      )}

      {/* Optional: Show a small map preview (requires Google Maps API key) */}
      {/* Uncomment when you have an API key
      {coordinates && (
        <div className="rounded-lg overflow-hidden border border-gray-200">
          <img 
            src={getMapThumbnailUrl()} 
            alt="Location preview" 
            className="w-full h-48 object-cover"
          />
        </div>
      )}
      */}
    </div>
  );
};

export default LocationDisplay;